/**
 * Vorgangsarten des administrativen Änderungsprotokolls (§29.1).
 *
 * Die vollständige Liste steht hier, damit die Protokollansicht in Stufe S8 dieselben Werte
 * beschriftet, die die Fachlogik schreibt. Nicht jede Art wird in jeder Stufe bereits erzeugt.
 *
 * Dieses Modul enthält ausschließlich Konstanten und Typen. Ein Protokolleintrag darf niemals
 * Passwörter, Schlüssel, vollständige Tokens, konkrete Stimmauswahlen oder eine Verknüpfung
 * zwischen Token und Auswahl enthalten (§29.2).
 */
export const VORGANGSART = {
    // Administratorzugang (§29.1, §5)
    ADMIN_ANMELDUNG: 'admin.anmeldung',
    ADMIN_ABMELDUNG: 'admin.abmeldung',
    ADMIN_SITZUNG_ABGELAUFEN: 'admin.sitzung_abgelaufen',
    ADMIN_SITZUNG_UEBERNOMMEN: 'admin.sitzung_uebernommen',
    ADMIN_ANMELDUNG_GESPERRT: 'admin.anmeldung_gesperrt',
    // Veranstaltungen (§6)
    VERANSTALTUNG_ERSTELLT: 'veranstaltung.erstellt',
    VERANSTALTUNG_GEAENDERT: 'veranstaltung.geaendert',
    VERANSTALTUNG_DUPLIZIERT: 'veranstaltung.dupliziert',
    VERANSTALTUNG_FREIGEGEBEN: 'veranstaltung.freigegeben',
    VERANSTALTUNG_BEENDET: 'veranstaltung.beendet',
    VERANSTALTUNG_ARCHIVIERT: 'veranstaltung.archiviert',
    VERANSTALTUNG_GELOESCHT: 'veranstaltung.geloescht',
    // Tokens (§7, §8)
    TOKENS_ERZEUGT: 'token.erzeugt',
    TOKEN_GESPERRT: 'token.gesperrt',
    TOKEN_ERSETZT: 'token.ersetzt',
    TOKENS_EXPORTIERT: 'token.exportiert',
    // Teilnehmer und Sitzungen (§9.8)
    TEILNEHMER_ANGEMELDET: 'teilnehmer.angemeldet',
    TEILNEHMER_ABGEMELDET: 'teilnehmer.abgemeldet',
    TEILNEHMER_SITZUNG_BEENDET: 'teilnehmer.sitzung_beendet',
    TEILNEHMER_SITZUNG_UEBERNOMMEN: 'teilnehmer.sitzung_uebernommen',
    // Abstimmungen (§10, §17)
    ABSTIMMUNG_ERSTELLT: 'abstimmung.erstellt',
    ABSTIMMUNG_GEAENDERT: 'abstimmung.geaendert',
    ABSTIMMUNG_GELOESCHT: 'abstimmung.geloescht',
    ABSTIMMUNG_BEREIT: 'abstimmung.bereit',
    ABSTIMMUNG_GEOEFFNET: 'abstimmung.geoeffnet',
    ABSTIMMUNG_GESCHLOSSEN: 'abstimmung.geschlossen',
    ABSTIMMUNG_ANNULLIERT: 'abstimmung.annulliert',
    // Ergebnisse (§18)
    ERGEBNIS_VEROEFFENTLICHT: 'ergebnis.veroeffentlicht',
    ERGEBNIS_ANZEIGE_BEENDET: 'ergebnis.anzeige_beendet',
    ERGEBNISFESTSTELLUNG_GESETZT: 'ergebnis.feststellung_gesetzt',
    ERGEBNISFESTSTELLUNG_GEAENDERT: 'ergebnis.feststellung_geaendert',
    ERGEBNIS_BERICHTIGUNG: 'ergebnis.berichtigung',
    // Exporte (§27)
    EXPORT_ERSTELLT: 'export.erstellt',
    EXPORT_GELOESCHT: 'export.geloescht',
    // Backups (§28)
    BACKUP_ERSTELLT: 'backup.erstellt',
    BACKUP_GELOESCHT: 'backup.geloescht',
    WIEDERHERSTELLUNG_DURCHGEFUEHRT: 'backup.wiederherstellung',
    SICHERUNGSSTAND_ERSTELLT: 'backup.sicherungsstand_erstellt',
    SICHERUNGSSTAND_GELOESCHT: 'backup.sicherungsstand_geloescht',
    // Systemvorgänge (§29.1, §31.2, §35.3, §36)
    SYSTEM_START: 'system.start',
    SYSTEM_HERUNTERFAHREN: 'system.herunterfahren',
    SYSTEM_UNERWARTETER_NEUSTART: 'system.unerwarteter_neustart',
    SYSTEM_DATENBANK_ANGELEGT: 'system.datenbank_angelegt',
    SYSTEM_MIGRATION: 'system.migration',
    SYSTEM_INTEGRITAETSPRUEFUNG: 'system.integritaetspruefung',
    /**
     * Vollständiges Zurücksetzen des Systems (ÄA-3).
     *
     * Der Vorgang löscht auch das Änderungsprotokoll. Der Eintrag über ihn ist deshalb der **erste**
     * des dann leeren Protokolls — und der einzige Nachweis, dass zurückgesetzt wurde. Ein
     * Fehlversuch mit falschem Schlüssel wird mit derselben Vorgangsart, aber ohne Erfolg vermerkt;
     * er löscht nichts und bleibt darum stehen.
     */
    SYSTEM_ZURUECKGESETZT: 'system.zurueckgesetzt',
    /**
     * Personalisierung (ÄA-5).
     *
     * Die Rechtstexte selbst gehören **nicht** in die Beschreibung — sie können lang sein, und das
     * Protokoll ist ein Nachweis darüber, **dass** und **von wem** geändert wurde (§29.2), nicht
     * eine zweite Ablage des Inhalts.
     */
    PERSONALISIERUNG_GEAENDERT: 'personalisierung.geaendert',
    PERSONALISIERUNG_LOGO_GESETZT: 'personalisierung.logo_gesetzt',
    PERSONALISIERUNG_LOGO_ENTFERNT: 'personalisierung.logo_entfernt',
    /**
     * E-Mail-Versand der Zugangskarten (ÄA-7).
     *
     * Ein einziger zusammenfassender Eintrag je Versandvorgang — Anzahl gesamt, erfolgreich,
     * fehlgeschlagen. **Keine** Adresse, kein Token: Nichts darf erkennen lassen, welcher Token an
     * welche Adresse ging.
     */
    TOKEN_EMAIL_VERSENDET: 'token.email_versendet',
    EMAIL_EINSTELLUNGEN_GEAENDERT: 'email_einstellungen.geaendert',
};
/** Alle Vorgangsarten als Feld, etwa für Filterlisten in Stufe S8 (§29.3). */
export const VORGANGSARTEN = Object.values(VORGANGSART);
/**
 * Deutsche Beschriftung je Vorgangsart (§37).
 *
 * Sie steht hier und nicht in der Oberfläche, weil dieselbe Beschriftung in der Protokollansicht,
 * im CSV-Export und im PDF-Export erscheinen muss. Ein Test verlangt für jede Vorgangsart einen
 * Eintrag; der `Record`-Typ erzwingt ihn zusätzlich beim Übersetzen.
 */
export const VORGANGSART_BESCHRIFTUNG = {
    [VORGANGSART.ADMIN_ANMELDUNG]: 'Adminanmeldung',
    [VORGANGSART.ADMIN_ABMELDUNG]: 'Adminabmeldung',
    [VORGANGSART.ADMIN_SITZUNG_ABGELAUFEN]: 'Adminsitzung abgelaufen',
    [VORGANGSART.ADMIN_SITZUNG_UEBERNOMMEN]: 'Adminsitzung übernommen',
    [VORGANGSART.ADMIN_ANMELDUNG_GESPERRT]: 'Adminanmeldung gesperrt',
    [VORGANGSART.VERANSTALTUNG_ERSTELLT]: 'Veranstaltung erstellt',
    [VORGANGSART.VERANSTALTUNG_GEAENDERT]: 'Veranstaltung geändert',
    [VORGANGSART.VERANSTALTUNG_DUPLIZIERT]: 'Veranstaltung dupliziert',
    [VORGANGSART.VERANSTALTUNG_FREIGEGEBEN]: 'Veranstaltung freigegeben',
    [VORGANGSART.VERANSTALTUNG_BEENDET]: 'Veranstaltung beendet',
    [VORGANGSART.VERANSTALTUNG_ARCHIVIERT]: 'Veranstaltung archiviert',
    [VORGANGSART.VERANSTALTUNG_GELOESCHT]: 'Veranstaltung gelöscht',
    [VORGANGSART.TOKENS_ERZEUGT]: 'Tokens erzeugt',
    [VORGANGSART.TOKEN_GESPERRT]: 'Token gesperrt',
    [VORGANGSART.TOKEN_ERSETZT]: 'Token ersetzt',
    [VORGANGSART.TOKENS_EXPORTIERT]: 'Tokens ausgegeben',
    [VORGANGSART.TEILNEHMER_ANGEMELDET]: 'Teilnehmer angemeldet',
    [VORGANGSART.TEILNEHMER_ABGEMELDET]: 'Teilnehmer abgemeldet',
    [VORGANGSART.TEILNEHMER_SITZUNG_BEENDET]: 'Teilnehmersitzung beendet',
    [VORGANGSART.TEILNEHMER_SITZUNG_UEBERNOMMEN]: 'Teilnehmersitzung übernommen',
    [VORGANGSART.ABSTIMMUNG_ERSTELLT]: 'Abstimmung erstellt',
    [VORGANGSART.ABSTIMMUNG_GEAENDERT]: 'Abstimmung geändert',
    [VORGANGSART.ABSTIMMUNG_GELOESCHT]: 'Abstimmung gelöscht',
    [VORGANGSART.ABSTIMMUNG_BEREIT]: 'Abstimmung als bereit markiert',
    [VORGANGSART.ABSTIMMUNG_GEOEFFNET]: 'Abstimmung geöffnet',
    [VORGANGSART.ABSTIMMUNG_GESCHLOSSEN]: 'Abstimmung geschlossen',
    [VORGANGSART.ABSTIMMUNG_ANNULLIERT]: 'Abstimmung annulliert',
    [VORGANGSART.ERGEBNIS_VEROEFFENTLICHT]: 'Ergebnis veröffentlicht',
    [VORGANGSART.ERGEBNIS_ANZEIGE_BEENDET]: 'Ergebnisanzeige beendet',
    [VORGANGSART.ERGEBNISFESTSTELLUNG_GESETZT]: 'Ergebnisfeststellung eingetragen',
    [VORGANGSART.ERGEBNISFESTSTELLUNG_GEAENDERT]: 'Ergebnisfeststellung geändert',
    [VORGANGSART.ERGEBNIS_BERICHTIGUNG]: 'Berichtigungsvermerk',
    [VORGANGSART.EXPORT_ERSTELLT]: 'Export erstellt',
    [VORGANGSART.EXPORT_GELOESCHT]: 'Exportdatei gelöscht',
    [VORGANGSART.BACKUP_ERSTELLT]: 'Backup erstellt',
    [VORGANGSART.BACKUP_GELOESCHT]: 'Backup gelöscht',
    [VORGANGSART.WIEDERHERSTELLUNG_DURCHGEFUEHRT]: 'Wiederherstellung durchgeführt',
    [VORGANGSART.SICHERUNGSSTAND_ERSTELLT]: 'Sicherungsstand erstellt',
    [VORGANGSART.SICHERUNGSSTAND_GELOESCHT]: 'Sicherungsstand gelöscht',
    [VORGANGSART.SYSTEM_START]: 'Anwendung gestartet',
    [VORGANGSART.SYSTEM_HERUNTERFAHREN]: 'Anwendung heruntergefahren',
    [VORGANGSART.SYSTEM_UNERWARTETER_NEUSTART]: 'Unerwarteter Neustart',
    [VORGANGSART.SYSTEM_DATENBANK_ANGELEGT]: 'Datenbank angelegt',
    [VORGANGSART.SYSTEM_MIGRATION]: 'Datenbankmigration',
    [VORGANGSART.SYSTEM_INTEGRITAETSPRUEFUNG]: 'Integritätsprüfung',
    [VORGANGSART.SYSTEM_ZURUECKGESETZT]: 'System zurückgesetzt',
    [VORGANGSART.PERSONALISIERUNG_GEAENDERT]: 'Personalisierung geändert',
    [VORGANGSART.PERSONALISIERUNG_LOGO_GESETZT]: 'Firmenlogo hinterlegt',
    [VORGANGSART.PERSONALISIERUNG_LOGO_ENTFERNT]: 'Firmenlogo entfernt',
    [VORGANGSART.TOKEN_EMAIL_VERSENDET]: 'Tokens per E-Mail versendet',
    [VORGANGSART.EMAIL_EINSTELLUNGEN_GEAENDERT]: 'E-Mail-Einstellungen geändert',
};
/** Deutsche Beschriftung je Herkunft (§29.2, §37). */
export const HERKUNFT_BESCHRIFTUNG = {
    admin: 'Administrator',
    teilnehmer: 'Teilnehmer',
    system: 'System',
};
/**
 * Herkunft eines Vorgangs.
 *
 * `system` steht für automatische Vorgänge ohne Anfragebezug, etwa Migrationen oder die
 * Erkennung eines unerwarteten Neustarts.
 */
export const PROTOKOLL_HERKUNFT = {
    ADMIN: 'admin',
    TEILNEHMER: 'teilnehmer',
    SYSTEM: 'system',
};
/** Voreinstellung und Obergrenze der Seitengröße. */
export const PROTOKOLL_PRO_SEITE_STANDARD = 50;
export const PROTOKOLL_PRO_SEITE_MAX = 200;
/**
 * Obergrenze eines Protokollexports.
 *
 * Das Protokoll wächst dauerhaft (§29.3, keine automatische Löschung). Ohne Obergrenze müsste ein
 * Export irgendwann den gesamten Bestand in den Speicher laden. Wer mehr braucht, grenzt den
 * Zeitraum ein — die Ausgabe weist auf die Begrenzung hin.
 */
export const PROTOKOLL_EXPORT_MAX = 5000;
//# sourceMappingURL=audit.js.map